import { useAuth } from '@/lib/auth/use-auth.js';
import { isEmbedded } from '@/lib/embed/embed-protocol.js';
import { tokens } from '@/lib/tokens.js';
import { useNavigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useEffect } from 'react';

interface ProtectedRouteProps {
  children: ReactNode;
}

function LoadingSpinner() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        minHeight: '200px',
        color: tokens.colors.textMutedDark,
        fontSize: '0.875rem',
      }}
    >
      <span>Loading…</span>
    </div>
  );
}

function WaitingForParent() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        minHeight: '200px',
        color: tokens.colors.textMutedDark,
        fontSize: '0.875rem',
        textAlign: 'center',
        padding: '2rem',
      }}
    >
      <p>Waiting for authentication from parent…</p>
    </div>
  );
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { status } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (status === 'unauthenticated' && !isEmbedded()) {
      void navigate({ to: '/login', replace: true });
    }
  }, [status, navigate]);

  if (status === 'authenticated') {
    return <>{children}</>;
  }

  if (status === 'idle' || status === 'authenticating') {
    return <LoadingSpinner />;
  }

  if (isEmbedded()) {
    return <WaitingForParent />;
  }

  // Standalone unauthenticated: show spinner while the redirect fires
  return <LoadingSpinner />;
}
